import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { API_BASE } from '../../lib/utils';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, CheckCircle, Clock, MapPin, Briefcase, ChevronRight, Trophy, Lock, Zap, Send } from 'lucide-react';

const SavedRoles = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeRole, setActiveRole] = useState(null);
  const [roleAssessments, setRoleAssessments] = useState([]);
  const [currentAssessment, setCurrentAssessment] = useState(null);
  const [currentQuestionIdx, setCurrentQuestionIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [roleResults, setRoleResults] = useState({});
  const [quickRunning, setQuickRunning] = useState(false);

  const fetchRoles = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/saved-roles/${user.id}`);
      setRoles(res.data.roles || []);
    } catch (error) {
      console.error('Failed to fetch saved roles:', error);
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  const allRolesComplete = roles.length > 0 && roles.every(r => {
    const total = r.assessment_count || 0;
    const completed = r.completed_assessments || 0;
    return total > 0 && completed >= total;
  });

  const hasAnyQualified = roles.some(r => r.overall_score !== null && r.overall_score >= 80);

  const handleQuickCompleteRoles = async () => {
    setQuickRunning(true);
    try {
      await axios.post(`${API_BASE}/demo/quick-complete-roles/${user.id}`);
      toast.success('All role assessments auto-completed!');
      await fetchRoles();
    } catch (error) {
      toast.error('Quick complete failed');
    } finally {
      setQuickRunning(false);
    }
  };

  const openRole = async (role) => {
    setActiveRole(role);
    try {
      const [assessRes, resultsRes] = await Promise.all([
        axios.get(`${API_BASE}/assessments/role/${role.id}`),
        axios.get(`${API_BASE}/role-assessment-results/${user.id}/${role.id}`),
      ]);
      setRoleAssessments(assessRes.data.assessments || []);
      setRoleResults(prev => ({
        ...prev,
        [role.id]: {
          results: resultsRes.data.results || [],
          overall_score: resultsRes.data.overall_score,
          completed: resultsRes.data.completed,
          total: resultsRes.data.total_assessments,
        }
      }));
    } catch (error) {
      console.error('Failed to fetch role assessments:', error);
    }
  };

  const startAssessment = (assessment) => {
    setCurrentAssessment(assessment);
    setCurrentQuestionIdx(0);
    setAnswers({});
  };

  const selectAnswer = (questionId, answer) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const submitAssessment = async () => {
    setSubmitting(true);
    const answersArray = Object.entries(answers).map(([questionId, answer]) => ({
      question_id: questionId,
      answer,
    }));
    try {
      const res = await axios.post(`${API_BASE}/assessments/role/submit`, {
        user_id: user.id,
        role_id: activeRole.id,
        assessment_id: currentAssessment.id,
        answers: answersArray,
      });
      toast.success(`Assessment completed! Score: ${res.data.result.score}%`);
      setCurrentAssessment(null);
      openRole(activeRole);
      fetchRoles();
    } catch (error) {
      toast.error('Failed to submit assessment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-12 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0071e3]" />
      </div>
    );
  }

  // =========== TAKING A ROLE ASSESSMENT ===========
  if (currentAssessment && activeRole) {
    const questions = currentAssessment.questions;
    const q = questions[currentQuestionIdx];
    const totalQ = questions.length;
    const answeredCount = Object.keys(answers).length;

    return (
      <div className="p-4 sm:p-8 md:p-12">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <button
              onClick={() => setCurrentAssessment(null)}
              className="p-2 rounded-xl hover:bg-[#f5f5f7] transition-colors"
              data-testid="back-to-role-btn"
            >
              <ArrowLeft className="w-5 h-5 text-[#86868b]" />
            </button>
            <div>
              <p className="text-sm text-[#86868b]">{activeRole.title}</p>
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#1d1d1f]">{currentAssessment.title}</h2>
            </div>
          </div>

          <div className="mb-8">
            <div className="flex justify-between text-sm text-[#86868b] mb-2">
              <span>Question {currentQuestionIdx + 1} of {totalQ}</span>
              <span>{answeredCount} answered</span>
            </div>
            <div className="w-full h-1.5 bg-[#f5f5f7] rounded-full overflow-hidden">
              <div
                className="h-full bg-[#FF9500] rounded-full transition-all duration-500"
                style={{ width: `${((currentQuestionIdx + 1) / totalQ) * 100}%` }}
              />
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-[#d2d2d7] p-6 sm:p-8 mb-6">
            <h3 className="text-lg sm:text-xl font-semibold text-[#1d1d1f] mb-6" data-testid="role-question-text">
              {q.question}
            </h3>
            <div className="space-y-3">
              {q.options.map((option, idx) => {
                const isSelected = answers[q.id] === option;
                return (
                  <button
                    key={idx}
                    onClick={() => selectAnswer(q.id, option)}
                    className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                      isSelected
                        ? 'border-[#FF9500] bg-orange-50 text-[#1d1d1f]'
                        : 'border-[#d2d2d7] bg-white text-[#1d1d1f] hover:border-[#86868b]'
                    }`}
                    data-testid={`role-option-${idx}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        isSelected ? 'border-[#FF9500] bg-[#FF9500]' : 'border-[#d2d2d7]'
                      }`}>
                        {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                      </div>
                      <span className="text-sm sm:text-base">{option}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Button
              onClick={() => setCurrentQuestionIdx(Math.max(0, currentQuestionIdx - 1))}
              disabled={currentQuestionIdx === 0}
              variant="ghost"
              className="rounded-full text-[#86868b]"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Previous
            </Button>
            {currentQuestionIdx < totalQ - 1 ? (
              <Button
                onClick={() => setCurrentQuestionIdx(currentQuestionIdx + 1)}
                disabled={!answers[q.id]}
                className="bg-[#FF9500] hover:bg-[#E68600] text-white rounded-full px-6"
                data-testid="role-next-btn"
              >
                Next <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={submitAssessment}
                disabled={answeredCount < totalQ || submitting}
                className="bg-[#FF9500] hover:bg-[#E68600] text-white rounded-full px-6"
                data-testid="role-submit-btn"
              >
                {submitting ? 'Submitting...' : 'Complete Assessment'}
                {!submitting && <ArrowRight className="w-4 h-4 ml-2" />}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // =========== ROLE DETAIL VIEW ===========
  if (activeRole) {
    const rd = roleResults[activeRole.id] || {};
    const completedAssessmentIds = (rd.results || []).map(r => r.assessment_id);
    const overallScore = rd.overall_score || 0;
    const isQualified = overallScore >= 80 && rd.completed >= rd.total;

    return (
      <div className="p-4 sm:p-8 md:p-12">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <button
              onClick={() => { setActiveRole(null); setRoleAssessments([]); }}
              className="p-2 rounded-xl hover:bg-[#f5f5f7] transition-colors"
              data-testid="back-to-roles-btn"
            >
              <ArrowLeft className="w-5 h-5 text-[#86868b]" />
            </button>
            <div>
              <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-[#f5f5f7] text-[#86868b]">
                {activeRole.category}
              </span>
              <h1 className="text-3xl sm:text-4xl font-semibold text-[#1d1d1f] mt-1">{activeRole.title}</h1>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-[#d2d2d7] p-6 sm:p-8 mb-6">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-semibold text-[#1d1d1f] mb-2">Day-to-Day</h3>
                <p className="text-sm text-[#86868b] leading-relaxed">{activeRole.day_to_day}</p>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[#1d1d1f] mb-2">Work Environment</h3>
                <p className="text-sm text-[#86868b] leading-relaxed">{activeRole.lived_experience}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-[#f5f5f7]">
              <div className="flex items-center gap-1.5 text-sm text-[#86868b]">
                <Clock className="w-4 h-4" /> {activeRole.typical_hours}
              </div>
              <div className="flex items-center gap-1.5 text-sm text-[#86868b]">
                <Briefcase className="w-4 h-4" /> {activeRole.physical_demand}
              </div>
              <div className="flex items-center gap-1.5 text-sm text-[#86868b]">
                <MapPin className="w-4 h-4" /> {activeRole.work_environment}
              </div>
            </div>
          </div>

          {rd.completed > 0 && (
            <div className={`rounded-2xl p-6 mb-6 ${
              isQualified
                ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white'
                : 'bg-white border border-[#d2d2d7]'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className={`text-lg font-semibold ${isQualified ? 'text-white' : 'text-[#1d1d1f]'}`}>
                    {isQualified ? 'Qualified!' : 'Current Score'}
                  </h3>
                  <p className={`text-sm ${isQualified ? 'text-white/80' : 'text-[#86868b]'}`}>
                    {isQualified
                      ? 'You meet the threshold for employer matching in this role'
                      : 'Score 80% or higher to qualify for employer matching'}
                  </p>
                </div>
                <div className={`text-4xl font-bold ${isQualified ? 'text-white' : 'text-[#1d1d1f]'}`}>
                  {overallScore}%
                </div>
              </div>
            </div>
          )}

          <h2 className="text-xl font-semibold text-[#1d1d1f] mb-4">Role-Specific Assessments</h2>
          <div className="space-y-3">
            {roleAssessments.map((assessment) => {
              const isComplete = completedAssessmentIds.includes(assessment.id);
              const result = (rd.results || []).find(r => r.assessment_id === assessment.id);
              return (
                <div
                  key={assessment.id}
                  className={`bg-white rounded-xl border p-5 transition-all ${
                    isComplete ? 'border-green-200' : 'border-[#d2d2d7] hover:border-[#86868b]'
                  }`}
                  data-testid={`role-assessment-${assessment.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-[#1d1d1f]">{assessment.title}</h3>
                      <p className="text-sm text-[#86868b]">{assessment.description}</p>
                      <span className="text-xs text-[#86868b] mt-1 inline-block">{assessment.questions.length} questions</span>
                    </div>
                    <div className="flex items-center gap-3 ml-4">
                      {isComplete ? (
                        <div className="flex items-center gap-2">
                          <span className={`text-lg font-bold ${result?.score >= 80 ? 'text-green-600' : result?.score >= 60 ? 'text-blue-600' : 'text-[#86868b]'}`}>
                            {result?.score}%
                          </span>
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        </div>
                      ) : (
                        <Button
                          onClick={() => startAssessment(assessment)}
                          className="bg-[#FF9500] hover:bg-[#E68600] text-white rounded-full text-sm px-5"
                          data-testid={`start-role-assessment-${assessment.id}-btn`}
                        >
                          Start <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {roleAssessments.length === 0 && (
            <div className="bg-white rounded-2xl border border-[#d2d2d7] p-8 text-center">
              <Lock className="w-10 h-10 text-[#86868b] mx-auto mb-3" />
              <p className="text-[#86868b]">No role-specific assessments available for this career path yet.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // =========== SAVED ROLES LIST ===========
  return (
    <div className="p-4 sm:p-8 md:p-12">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-semibold text-[#1d1d1f] mb-3">
            Saved Roles
          </h1>
          <p className="text-lg text-[#86868b]">
            Complete the role-specific assessments to qualify for employer matching. You need a score of 80% or higher.
          </p>
        </div>

        {/* Demo quick-complete banner */}
        {roles.length > 0 && !allRolesComplete && (
          <div className="bg-[#1d1d1f] rounded-2xl p-5 sm:p-6 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="quick-complete-roles-card">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#FF9500] text-white">DEMO</span>
                <h3 className="text-white font-semibold text-base">Auto-complete all role assessments</h3>
              </div>
              <p className="text-white/60 text-sm">Skip ahead and instantly complete all assessments for your saved roles.</p>
            </div>
            <Button
              onClick={handleQuickCompleteRoles}
              disabled={quickRunning}
              className="bg-[#FF9500] hover:bg-[#E68600] text-white rounded-full px-6 flex-shrink-0"
              data-testid="quick-complete-roles-btn"
            >
              <Zap className="w-4 h-4 mr-2" />
              {quickRunning ? 'Completing...' : 'Quick Complete'}
            </Button>
          </div>
        )}

        {/* Go to Employer Matches banner */}
        {allRolesComplete && hasAnyQualified && (
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-6 sm:p-8 mb-8" data-testid="go-to-matches-card">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-white font-semibold text-xl mb-1">Role assessments complete!</h3>
                <p className="text-white/80 text-sm">You're qualified for employer matching. See which employers are looking for candidates like you.</p>
              </div>
              <Button
                onClick={() => navigate('/apprentice/matches')}
                className="bg-white text-green-700 hover:bg-green-50 rounded-full px-8 font-semibold flex-shrink-0"
                data-testid="go-to-matches-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                Go to Your Employer Matches
              </Button>
            </div>
          </div>
        )}

        {roles.length === 0 ? (
          <div className="bg-white rounded-2xl border border-[#d2d2d7] p-12 text-center">
            <Briefcase className="w-12 h-12 text-[#86868b] mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-[#1d1d1f] mb-2">No saved roles yet</h3>
            <p className="text-[#86868b] mb-6">Complete your Career Discovery assessments to find roles that match your skills</p>
            <Button
              onClick={() => navigate('/apprentice/assessments')}
              className="bg-[#0071e3] hover:bg-[#0077ed] text-white rounded-full px-8"
              data-testid="go-to-assessments-btn"
            >
              Start Career Discovery
            </Button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {roles.map((role) => {
              const completedCount = role.completed_assessments || 0;
              const totalCount = role.assessment_count || 0;
              const score = role.overall_score;
              const isComplete = totalCount > 0 && completedCount >= totalCount;
              const isQualified = score !== null && score >= 80 && isComplete;

              return (
                <div
                  key={role.id}
                  onClick={() => openRole(role)}
                  className={`bg-white rounded-2xl border p-6 cursor-pointer transition-all hover:shadow-md ${
                    isQualified ? 'border-green-300' : 'border-[#d2d2d7] hover:border-[#86868b]'
                  }`}
                  data-testid={`saved-role-${role.id}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-[#f5f5f7] text-[#86868b]">
                      {role.category}
                    </span>
                    {isQualified && <Trophy className="w-5 h-5 text-green-600" />}
                  </div>
                  <h3 className="text-lg font-semibold text-[#1d1d1f] mb-2">{role.title}</h3>
                  <p className="text-sm text-[#86868b] mb-4 line-clamp-2">{role.day_to_day}</p>

                  {/* Score display */}
                  {isComplete && score !== null && (
                    <div className={`text-center py-3 mb-3 rounded-xl ${
                      isQualified ? 'bg-green-50' : 'bg-orange-50'
                    }`}>
                      <div className={`text-3xl font-bold ${isQualified ? 'text-green-600' : 'text-[#FF9500]'}`}>
                        {score}%
                      </div>
                      <div className={`text-xs font-medium ${isQualified ? 'text-green-600' : 'text-[#FF9500]'}`}>
                        {isQualified ? 'Qualified' : 'Below 80% threshold'}
                      </div>
                    </div>
                  )}

                  {/* Progress bar */}
                  {!isComplete && (
                    <div className="mb-3">
                      <div className="flex justify-between text-xs text-[#86868b] mb-1">
                        <span>{completedCount}/{totalCount} assessments</span>
                      </div>
                      <div className="w-full h-1.5 bg-[#f5f5f7] rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#FF9500] rounded-full transition-all"
                          style={{ width: totalCount > 0 ? `${(completedCount / totalCount) * 100}%` : '0%' }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex items-center text-sm text-[#0071e3] font-medium">
                    {isQualified ? 'View Results' : isComplete ? 'View Score' : completedCount > 0 ? 'Continue' : 'Start Assessments'}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SavedRoles;
